import { Injectable } from '@angular/core';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { IEstimate, IRoom } from './esimtate.model';
import { NGXLogger as LoggerService } from "ngx-logger";
import { EstimateFdbService } from './estimate.fdb.service';
import { map } from 'rxjs/operators';
import { GoogleAnalyticsService } from '../common/google-analytics.service';
import { PresenceService } from '../user/online-presence.service';
import { TimerService } from '../timer/timer.service';
import { SnapshotAction, snapshotChanges } from '@angular/fire/compat/database';
import { StripeService } from '../user/services/stripe.service';
const DEFAULT_OPTIONS: string[] = ['?', 'cof', '0', '0.5', '1', '2', '3', '5', '8', '13', '20', '40', '100'];


@Injectable()
export class EstimateService {
    private roomId = '';
    public displayedColumns: string[] = ['displayName', 'storyPoints'];
    private roomSubscription: Subscription;
    private estimatesSubscription: Subscription;
    private showHideSubscription: Subscription
    public estimateOptions: string[];
    public showEstimates = false;
    public allowHide = true;
    public allowClear = true;
    private isOwnerAnonymous: boolean;
    public allowDeleteEstimates = true;
    public userPresence = true;
    public showAverage = true;
    public showMedian = true;
    public estimates: IEstimate[] = [];
    public userStoryPoints: string = null;
    private uid: string = null;
    public isLoading = true;
    public alertIsClosed = false;
    public averageEstimate: string;
    public medianEstimate: string;
    public showTimer = true;
    public ownerUid: string;
    public ownerHasPremium: boolean;

    constructor(
        private estimateFireService: EstimateFdbService,
        private logger: LoggerService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private presenceService: PresenceService,
        private timeService: TimerService,
        private stripeService: StripeService,

    ) {
        this.roomId = '';
        this.estimateOptions = this.getDefaultEstimateOptions();
    }

    public async generateRoom(uid: string, isOwnerAnonymous: boolean): Promise<string> {
        // radonmly generate a code and then check if does not  exist already then create the room
        const length = 8;
        let loopCount = 0;
        let roomId = '';
        while (true) {
            roomId = this.makeId(length);
            this.logger.log('roomId: ' + roomId);
            this.logger.log('await');
            const isExistentRoom = await this.isExistentRoom(roomId);
            this.logger.log('await');
            if (!isExistentRoom) {
                await this.createRoom(uid, roomId, isOwnerAnonymous);
                return roomId;
            }
            loopCount = loopCount + 1;
            if (loopCount > 100) { throw Error('No free room'); }
        }
    }

    private makeId(length): string {
        let result = '';
        //  var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const characters = '0123456789';
        const charactersLength = characters.length;
        for (let i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    public async createRoom(uid: string, roomId: string, isOwnerAnonymous: boolean): Promise<void> {
        await this.estimateFireService.createRoom(uid, roomId, isOwnerAnonymous);
    }

    public async createEstimate(roomId: string, developerName: string, uid: string, estimate?: string): Promise<void> {
        const isExistentRoom = await this.isExistentRoom(roomId); // that's not need I guess
        if (isExistentRoom) {
            if (this.isOwnerAnonymous) {
                this.googleAnalyticsService.eventEmitter('submitEstimate isOwnerAnonymous', 'Poker Room', 'submitEstimate Action');
            } else {
                this.googleAnalyticsService.eventEmitter('submitEstimate NOT isOwnerAnonymous', 'Poker Room', 'submitEstimate Action');
            }
            await this.estimateFireService.createEstimate(roomId, developerName, uid, estimate);
        } else {
            const error = new Error('The estimate could not be submitted. The room with id ' + roomId + ' does not exist (anymore).');
            throw (error);
        }
    }

    // this is the function the must be called always when a user wants to enter a room
    public async registerUserInRoom(roomId: string, developerName: string, uid: string): Promise<void> {
        this.logger.log('estimateService enterRoom: ' + roomId + ' ; name:' + developerName + ' ; uid: ' + uid);
        this.uid = uid;
        this.isLoading = true;
        if (this.roomId !== roomId) {
            if (this.roomSubscription) {
                this.roomSubscription.unsubscribe();
            }
            if (this.estimatesSubscription) {
                this.estimatesSubscription?.unsubscribe();
            }
        } else {
            this.logger.log('room id has not changed this.roomId: ' + this.roomId + '; roomId: ' + roomId);
            return;
        }
        this.logger.log('estiamteService enterRoom subscribe ');
        this.logger.log('ROOM ID change ');
        this.roomId = roomId;
        this.roomSubscription = this.setRoomObserver(roomId);
        this.showHideSubscription = this.setShowHideObserver(roomId);
        this.estimatesSubscription = this.setEstimatesObserver(roomId);
        await this.createEstimate(roomId, developerName, uid);
        this.timeService.connectRoom(roomId);
    }

    async getOwnerHasPremium(roomId: string): Promise<boolean> {
        const room = await firstValueFrom(this.getRoomObservable(roomId));
        if (room) {
            this.ownerUid = room.ownerUid;
        }
        if (!this.ownerUid) {
            this.logger.error('getOwnerHasPremium: ownerUid ist noch nicht gesetzt');
            return false;
        }
        this.ownerHasPremium = await this.stripeService.getUserHasPremium(this.ownerUid);
        return this.ownerHasPremium;
    }

    private setShowHideObserver(roomId: string): Subscription {
        this.logger.log('setShowHideObserver: ' + roomId);
        return this.estimateFireService.getShowHideObservable(roomId).pipe(
            map(object => {
                if (object) {
                    this.showEstimates = object.showEstimates;
                    if (this.showEstimates) {
                        this.logger.log('setShowHideObserver showEstimtates');
                        this.estimates = [...this.estimates.sort(this.compareEstimates)]; // we sort only when showing the estimates
                    }
                    else {
                        this.showEstimates = false
                    }
                }
            })).subscribe()
    }

    private setRoomObserver(roomId: string) {
        this.logger.log('setRoomObserver: ' + roomId);
        return this.roomSubscription = this.getRoomObservable(roomId).pipe(
            map(room => {
                this.logger.log('room observer ', room);
                if (room) {
                    if (this.ownerUid !== room.ownerUid) {
                        this.ownerUid = room.ownerUid;
                        this.getOwnerHasPremium(roomId);
                    }
                    this.isLoading = false;
                    if (room.estimateOptions) {
                        this.estimateOptions = room.estimateOptions.split(',');
                    } else {
                        this.estimateOptions = this.getDefaultEstimateOptions();
                    }
                    if (room.allowHide === 'no') {
                        this.allowHide = false;
                    } else {
                        this.allowHide = true;
                    }
                    this.isOwnerAnonymous = room.isOwnerAnonymous;
                    if (room.allowClear === 'no') {
                        this.allowClear = false;
                    } else {
                        this.allowClear = true;
                    }
                    if (room.allowDeleteEstimates === 'no') {
                        this.allowDeleteEstimates = false;
                    } else {
                        this.allowDeleteEstimates = true;
                    }
                    if (room.userPresence === 'yes') {
                        this.logger.log('userpresesnce yes');
                        this.presenceService.setConnectionCallback(this.uid);
                        this.userPresence = true;
                    } else {
                        this.userPresence = false;
                    }
                    if (room.showAverage === 'yes') {
                        this.logger.log('showAverage yes');
                        this.showAverage = true;
                    } else {
                        this.showAverage = false;
                    }
                    if (room.showMedian === 'yes') {
                        this.logger.log('showMedian yes');
                        this.showMedian = true;
                    } else {
                        this.showMedian = false;
                    }
                    if (room.showTimer === 'no') {
                        this.logger.log('showMedian yes');
                        this.showTimer = false;
                    } else {
                        this.showTimer = true;
                    }
                } else {
                    this.logger.warn('The room ' + roomId + '  does not exist (anymore). Please choose a different one.');
                }
            })).subscribe();
    }

    private setEstimatesObserver(roomId: string): Subscription {
        this.logger.log('setEstimatesObserver: ' + roomId);
        return this.estimateFireService.getEstimatesObservable(roomId).pipe(
            map(snapshotList => {
                this.logger.log(snapshotList);
                this.setEstimates(snapshotList);
                this.setUsersEstimate();
                this.averageEstimate = this.getAverageEstimate();
                this.medianEstimate = this.getMedian();
                if (this.showEstimates) {
                    this.estimates = this.estimates.sort(this.compareEstimates);
                }
                this.logger.log('setEstimatesObserver', this.estimates);
            })
        ).subscribe();
    };

    public getMedian(): string {
        let estimateNumbers: number[] = [];
        const estimatesCopy = [...this.estimates]
        estimatesCopy.sort(this.compareEstimates)
        estimatesCopy.forEach((value) => {
            const valueNumber = parseFloat(value.storyPoints);
            if (valueNumber !== undefined && valueNumber !== null && !isNaN(valueNumber)) {
                estimateNumbers.push(valueNumber)
            }
        });
        if (estimateNumbers.length === 0) { return ""; }
        else if (estimateNumbers.length % 2 === 0) {
            //even
            const lower = estimateNumbers[(estimateNumbers.length / 2) - 1]
            const higher = estimateNumbers[estimateNumbers.length / 2]
            return "" + ((higher + lower) / 2)
        } else {
            //not even
            return "" + estimateNumbers[(estimateNumbers.length / 2) - 0.5]
        }
    }

    private setEstimates(snapshotChangesArray: SnapshotAction<any>[]) {
        this.estimates = []// create a new a array to make sure the table updates
        snapshotChangesArray.forEach(snapshotChange => {
            const estimate: IEstimate = { ...snapshotChange.payload.val() };
            estimate.userUid = snapshotChange.key // we dont persist the uid to reduce firebase cost
            this.estimates.push(estimate);
        });
        this.logger.log('setEstimates', this.estimates);
    }

    getAverageEstimate(): string {
        if (this.estimates) {
            this.logger.log('get averageEstimate')
            let returnValue = 0;
            let index = 0;
            this.estimates.forEach((value) => {
                const valueNumber = parseFloat(value.storyPoints);
                if (valueNumber !== undefined && valueNumber !== null && !isNaN(valueNumber)) {
                    //  this.logger.error('averageEstimate, forEach', valueNumber);
                    index = index + 1;
                    returnValue = returnValue + valueNumber;
                }
            });
            if (index !== 0) {
                return (Math.round(returnValue * 10 / index) / 10).toString();
            }
        }
        return '';
    }

    private setUsersEstimate() {
        this.userStoryPoints = null;
        this.estimates.forEach(estimate => {
            this.logger.log('setUserEstimate', this.uid, estimate.userUid);
            if (this.uid === estimate.userUid) {
                this.userStoryPoints = estimate.storyPoints;
            }
        });
    }


    compareEstimates(estimateA: IEstimate, estimateB: IEstimate): number {
        // check empty case first
        const aIsEmpty = estimateA.storyPoints === undefined || estimateA.storyPoints === null || estimateA.storyPoints === ''
        const bIsEmpty = estimateB.storyPoints === undefined || estimateB.storyPoints === null || estimateB.storyPoints === ''
        if (aIsEmpty || bIsEmpty) {
            if (aIsEmpty && !bIsEmpty) {
                return -1;
            }
            if (!aIsEmpty && bIsEmpty) {
                return 1;
            }
            // both must be empty
            return 0
        }
        const a = parseFloat(estimateA.storyPoints);
        const b = parseFloat(estimateB.storyPoints);
        // case that at least one is not  a number
        if (isNaN(a) || isNaN(b)) {
            if (isNaN(a) && !isNaN(b)) {
                return -1;
            }
            if (!isNaN(a) && isNaN(b)) {
                return 1;
            }
            // both must be not a number
            if (estimateA.storyPoints < estimateB.storyPoints) {
                return -1;
            } else if (estimateA.storyPoints > estimateB.storyPoints) {
                return 1;
            } else {
                return 0;
            }
        }
        // both numbers
        if (a < b) {
            return -1;
        }
        if (a > b) {
            return 1;
        }
        return 0;
    }

    public unsubscribe() {
        this.roomId = '';
        this.estimateOptions = this.getDefaultEstimateOptions();
        this.estimatesSubscription?.unsubscribe();
        this.roomSubscription?.unsubscribe();
        this.showHideSubscription?.unsubscribe();
        this.timeService?.disconnnectRoom();
    }
    public async isExistentRoom(roomId): Promise<boolean> {
        return this.estimateFireService.isExistentRoom(roomId);
    }

    public getRoomObservable(roomId: string): Observable<IRoom> {
        return this.estimateFireService.getRoomObservable(roomId);
    }
    public async setShowEstimates(roomId: string, showEstimates: boolean): Promise<void> {
        try {
            this.estimateFireService.setShowEstimates(roomId, showEstimates);
        } catch (e) {
            const error = new Error('The room with id ' + roomId + ' does not exist (anymore). ' + e);
            throw (error);
        }
    }
    public clearStoryPoints(roomId: string): void {
        this.estimateFireService.clearStoryPoints(roomId);
    }
    public clearRoom(roomId: string): void {
        this.estimateFireService.clearRoom(roomId);
    }

    public deleteRoom(roomId: string) {
        this.estimateFireService.deleteRoom(roomId);
    }
    public deleteOldRooms(): void {
        this.estimateFireService.deleteOldRooms();
    }
    public getDefaultEstimateOptions(): string[] {
        return DEFAULT_OPTIONS;
    }
    public setRoomConfiguration(
        roomId: string,
        estimateOptions: string,
        allowHide: string,
        allowClear: string,
        allowDeleteEstimates: string,
        userPresence: string,
        showAverage: string,
        showMedian: string,
        showTimer: string)
        : void {
        this.logger.log('setRoomConfiguration: ' + estimateOptions);
        this.estimateFireService.setRoomConfiguration(
            roomId,
            estimateOptions,
            allowHide,
            allowClear,
            allowDeleteEstimates,
            userPresence,
            showAverage,
            showMedian,
            showTimer);
    }
}
